import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Users, BarChart3, Play, ArrowRight } from "lucide-react";

const SLIDES = [
  {
    img: "/generated/hero3.png",
    eyebrow: "Pediatric Behavioral Health Platform",
    title: ["Behavioral Health, ", "Built", " for Children, ", "Families", " & Care Teams"],
    tags: ["Assessments", "Telehealth", "Treatment Plans", "Family Portals"],
    sub: "AI-powered care coordination that connects clinicians, families, and schools — from first assessment to lasting progress.",
  },
  {
    img: "/generated/hero2.png",
    eyebrow: "One Connected Care Journey",
    title: ["From Assessment to ", "Progress", ", All in ", "One Place"],
    tags: ["Screeners", "Video Sessions", "Progress Tracking", "Care Team Chat"],
    sub: "Coordinate every clinician, parent, and teacher around the child — with insights that guide each next step.",
  },
  {
    img: "/generated/hero1.png",
    eyebrow: "Where Technology Meets Heart",
    title: ["Stronger ", "Children", ", Brighter ", "Tomorrows"],
    tags: ["Compassionate", "Proactive", "Evidence-Based", "Family-First"],
    sub: "Blending clinical expertise with warmth and technology, so every child can learn, grow, and thrive.",
  },
];

const TRUST = [
  [ShieldCheck, "HIPAA Compliant", "& Secure"],
  [Users, "Built for", "Pediatric Care"],
  [BarChart3, "Better Outcomes", "Brighter Futures"],
];

export default function HeroSlider() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback((i) => setActive(((i % SLIDES.length) + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setActive((p) => (p + 1) % SLIDES.length), 6000);
    return () => clearInterval(t);
  }, [paused]);

  const s = SLIDES[active];

  return (
    <section className="px-3 sm:px-5 lg:px-8 pt-4 lg:pt-6">
      <div
        className="relative overflow-hidden rounded-[26px] lg:rounded-[40px] mx-auto max-w-[1400px] shadow-[0_34px_90px_-34px_rgba(16,35,63,0.40)] ring-1 ring-white/60"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Slide backgrounds (crossfade) */}
        <div className="absolute inset-0 overflow-hidden">
          {SLIDES.map((sl, i) => (
            <img
              key={sl.img}
              src={sl.img}
              alt=""
              className={`absolute inset-0 w-full h-full object-cover object-right transition-opacity duration-[1200ms] ease-in-out ${i === active ? "opacity-100" : "opacity-0"}`}
              style={{ transform: i === active ? "scale(1.04)" : "scale(1.0)", transition: "opacity 1200ms ease-in-out, transform 7s ease-out" }}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/82 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-white/70 via-transparent to-transparent" />
        </div>

        {/* Content */}
        <div className="relative px-6 sm:px-9 lg:px-14 pt-12 lg:pt-16 pb-16 lg:pb-20 min-h-[540px] lg:min-h-[620px]">
          <div className="relative max-w-xl">
            <div className="flex items-center gap-3">
              <span className="h-px w-8 bg-[#1a5fd0]" />
              <p className="text-[12px] font-bold tracking-[0.24em] text-[#5b6b86] uppercase">{s.eyebrow}</p>
            </div>
            <h1 className="font-display mt-4 text-[40px] lg:text-[54px] leading-[1.03] font-extrabold text-[#10233f]">
              {s.title.map((part, i) => (i % 2 === 1 ? <span key={i} className="text-gradient-blue">{part}</span> : <React.Fragment key={i}>{part}</React.Fragment>))}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[16px] font-semibold text-[#33425c]">
              {s.tags.map((t, i) => (
                <React.Fragment key={t}>{i > 0 && <span className="text-[#1a5fd0]">•</span>}<span>{t}</span></React.Fragment>
              ))}
            </div>
            <p className="mt-5 text-[18px] leading-relaxed text-slate-600 max-w-lg">{s.sub}</p>
          </div>

          {/* Constant CTAs + trust (do not fade, stable) */}
          <div className="relative max-w-xl">
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <Link to="/request-demo" data-testid="hero-request-demo" className="btn-primary px-7 py-3.5 text-[16px] font-semibold inline-flex items-center gap-2">Request a Demo <ArrowRight size={18} /></Link>
              <button className="btn-ghost-blue px-6 py-3.5 text-[16px] font-semibold inline-flex items-center gap-2 bg-white/80 backdrop-blur"><span className="h-8 w-8 rounded-full bg-[#e8f1ff] grid place-items-center text-[#1a5fd0]"><Play size={14} className="fill-[#1a5fd0]" /></span> Watch Video</button>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-4">
              {TRUST.map(([Ic, a, b], i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="h-11 w-11 rounded-2xl bg-white/70 backdrop-blur grid place-items-center text-[#1a5fd0] shadow-sm"><Ic size={20} /></span>
                  <div className="text-[14px] font-semibold text-[#10233f] leading-tight">{a}<br /><span className="text-slate-500 font-medium">{b}</span></div>
                </div>
              ))}
            </div>

            {/* Slide indicators */}
            <div className="mt-9 flex items-center gap-2.5">
              {SLIDES.map((_, i) => (
                <button
                  key={i}
                  onClick={() => go(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  data-testid={`hero-dot-${i}`}
                  className={`h-2.5 rounded-full transition-all duration-300 ${i === active ? "w-8 bg-[#1a5fd0]" : "w-2.5 bg-[#1a5fd0]/30 hover:bg-[#1a5fd0]/60"}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
